import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { preloadLanguages } from "./i18n/config";

// Preload other languages after initial render work has settled.
if ("requestIdleCallback" in window) {
	window.requestIdleCallback(() => preloadLanguages(), { timeout: 2000 });
} else {
	window.setTimeout(() => preloadLanguages(), 500);
}

const GA_ID = "G-0589575CRB";

// Load analytics only after user interaction (scroll/click) - significantly reduces initial network activity
const loadAnalytics = () => {
	if (document.querySelector(`script[data-ga-id="${GA_ID}"]`)) return;

	(window as Window & { dataLayer?: unknown[] }).dataLayer = (window as Window & { dataLayer?: unknown[] }).dataLayer || [];

	const gtag = (...args: unknown[]) => {
		(window as Window & { dataLayer?: unknown[] }).dataLayer?.push(args);
	};

	gtag("js", new Date());
	gtag("config", GA_ID);

	const script = document.createElement("script");
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
	script.dataset.gaId = GA_ID;
	document.head.appendChild(script);
	
	// Remove event listeners after first interaction to prevent multiple loads
	window.removeEventListener('scroll', handleUserInteraction);
	window.removeEventListener('click', handleUserInteraction);
	window.removeEventListener('keydown', handleUserInteraction);
	window.removeEventListener('touchstart', handleUserInteraction);
};

// User interaction handler - loads analytics on first user engagement
const handleUserInteraction = () => {
	loadAnalytics();
};

// Load analytics only after user interaction (scroll, click, keypress, or touch)
// This ensures analytics doesn't compete with critical page resources
const scheduleAnalyticsLoad = () => {
	// Listen for any user interaction
	window.addEventListener('scroll', handleUserInteraction, { once: true, passive: true });
	window.addEventListener('click', handleUserInteraction, { once: true, passive: true });
	window.addEventListener('keydown', handleUserInteraction, { once: true, passive: true });
	window.addEventListener('touchstart', handleUserInteraction, { once: true, passive: true });
	
	// Fallback: Load after 10 seconds if no interaction (very conservative)
	setTimeout(() => {
		loadAnalytics();
	}, 10000);
};

// Schedule analytics - will load after user interaction
scheduleAnalyticsLoad();

// Clear the initial loader after React mounts
const clearInitialLoader = () => {
	const loader = document.querySelector('.initial-loader');
	if (loader) {
		loader.remove();
	}
};

// Use MutationObserver to detect when React mounts
const observer = new MutationObserver((mutations) => {
	for (const mutation of mutations) {
		for (const node of mutation.addedNodes) {
			if (node instanceof Element && node.id === 'root') {
				const root = node as HTMLElement;
				if (root.children.length > 1 || (root.children.length === 1 && !root.children[0].classList.contains('initial-loader'))) {
					clearInitialLoader();
					observer.disconnect();
					return;
				}
			}
		}
	}
});

observer.observe(document.getElementById('root')!, { childList: true, subtree: true });

createRoot(document.getElementById("root")!).render(<App />);

